import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Vote, Lock, ArrowRight, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSelector } from "../components/LanguageSelector";
import { PasswordStrength } from "../components/PasswordStrength";
import { toast } from "sonner";
import api from "../lib/api";

import { useAppConfig } from "../lib/AppConfigContext";

export default function ResetPasswordPage() {
  const { appName } = useAppConfig();
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/entities/verify-reset-token/${token}`);
      setValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }
    
    const hasMinLen = password.length >= 10;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNum = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(password);
    if (!hasMinLen || !hasUpper || !hasLower || !hasNum || !hasSpecial) {
      toast.error(t('passwordPolicy.weak'));
      return;
    }

    setLoading(true);

    try {
      await api.post("/entities/reset-password", { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      {/* Language selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Vote className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl text-slate-900">{appName}</h1>
            <p className="text-sm text-slate-500">{t('consulta.title')}</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          {!valid ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {t('auth.invalidResetLink')}
              </h2>
              <p className="text-slate-500 mb-6">
                {t('auth.invalidResetLinkMessage')}
              </p>
              <Link to="/forgot-password">
                <Button className="bg-slate-900 hover:bg-slate-800">
                  {t('auth.requestNewLink')}
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {t('auth.passwordUpdated')}
              </h2>
              <p className="text-slate-500 mb-6">
                {t('auth.passwordUpdatedMessage')}
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {t('auth.newPassword')}
              </h2>
              <p className="text-slate-500 mb-6">
                {t('auth.resetPasswordSubtitle')} <strong>{email}</strong>
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="form-group">
                  <Label htmlFor="password" className="form-label">
                    {t('auth.newPassword')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-11"
                      placeholder={t('resetPassword.passwordPlaceholder')}
                      required
                      data-testid="reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <PasswordStrength password={password} minLength={10} />
                </div>

                <div className="form-group">
                  <Label htmlFor="confirmPassword" className="form-label">
                    {t('auth.confirmNewPassword')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-11"
                      placeholder={t('auth.confirmPassword')}
                      required
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      {t('auth.updatePassword')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
